import { useState, useEffect } from 'react'
import {
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   ResponsiveContainer,
   LineChart,
   Line,
} from 'recharts';
import { analyticsService } from '@/services/analyticsService';
import toast from 'react-hot-toast'

interface ChartData {
  date: string
  fullDate: string
  users: number
  trips: number
  photos: number
}

export const ActivityChart = () => {
  const [chartData, setChartData] = useState<ChartData[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState('7d')

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setLoading(true)
        
        // Fetch data for all three metrics in parallel
        const [usersData, tripsData, photosData] = await Promise.all([
          analyticsService.getTimeSeries({ metric: 'users', period: selectedPeriod }),
          analyticsService.getTimeSeries({ metric: 'trips', period: selectedPeriod }),
          analyticsService.getTimeSeries({ metric: 'photos', period: selectedPeriod })
        ])

        // Combine the data into a single chart format
        const combinedData: ChartData[] = []
        const maxLength = Math.max(
          usersData.data.length,
          tripsData.data.length,
          photosData.data.length
        )

        for (let i = 0; i < maxLength; i++) {
          const date = usersData.data[i]?.date || tripsData.data[i]?.date || photosData.data[i]?.date
          if (date) {
            const fullDate = new Date(date)
            if (!isNaN(fullDate.getTime())) { // Check if date is valid
              combinedData.push({
                date: fullDate.toLocaleDateString('en-US', { weekday: 'short' }),
                fullDate: fullDate.toISOString().split('T')[0], // Store full date for tooltip
                users: usersData.data[i]?.value || 0,
                trips: tripsData.data[i]?.value || 0,
                photos: photosData.data[i]?.value || 0
              })
            }
          }
        }

        setChartData(combinedData)
      } catch (error: any) {
        console.error('Failed to fetch chart data:', error)
        const errorMessage = error.response?.data?.error || 'Failed to load chart data'
        toast.error(errorMessage)
        setChartData([]) // Set empty data instead of mock data
      } finally {
        setLoading(false)
      }
    }

    fetchChartData()
  }, [selectedPeriod])

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Weekly Activity</h3>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Activity Overview</h3>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>
      </div>
      {chartData.length > 0 ? (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                label={{ value: 'Date', position: 'insideBottom', offset: -5 }}
              />
              <YAxis 
                label={{ value: 'Count', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                formatter={(value: number, name: string) => [
                  value.toLocaleString(),
                  name.charAt(0).toUpperCase() + name.slice(1)
                ]}
                labelFormatter={(label, payload) => {
                  if (payload && payload[0] && payload[0].payload.fullDate) {
                    const date = new Date(payload[0].payload.fullDate)
                    if (!isNaN(date.getTime())) {
                      return date.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    }
                  }
                  return label
                }}
              />
              <Line type="monotone" dataKey="users" stroke="#3b82f6" strokeWidth={2} />
              <Line type="monotone" dataKey="trips" stroke="#8b5cf6" strokeWidth={2} />
              <Line type="monotone" dataKey="photos" stroke="#eab308" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center text-gray-500">
          No data available for the selected period
        </div>
      )}
      <div className="mt-4 flex justify-center space-x-6 text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
          <span className="text-gray-600">New Users</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
          <span className="text-gray-600">Trips Created</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
          <span className="text-gray-600">Photos Uploaded</span>
        </div>
      </div>
    </div>
  )
} 